import { useQuery } from "@tanstack/react-query";
import {
  ConnectionStatus,
  device,
  lifecycle,
  LoadedInfo,
  MobileNotification,
  OnResumeInfo,
  ScreenInfo,
  ui
} from "@workspace/mobile";
import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { errorHandler } from "../APIs/errorHandler";
import { offlineLocalStorageManager } from "../APIs/OfflineLocalStorageManager";
import type { LoadingStatus } from "../components/Shared/Modals/LoadingStatusModal/LoadingStatusModal";
import { useLoadingStatus } from "./useLoadingStatus";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  accessButtonEnabledChanged,
  accessButtonMenuItemsChanged,
  biometricEnabledChanged,
  biometricSupportedChanged,
  connectionStatusChanged,
  displayOrientationChanged,
  pinSecureChanged,
  selectConnectionStatus
} from "../store/slices/deviceSlice";
import {
  collinsUserChanged,
  selectSubscriptionId,
  subscriptionIdChanged,
  usersChanged
} from "../store/slices/sessionSlice";
import {
  getUserInfo,
  GraphResponse,
  RootQuery,
  User
} from "../services/workspaceAPI";
import { CollinsUser } from "../types/user";
import { dmnIdentity } from "../utils/dmnIdentity";
import { dmnStorage } from "../utils/dmnStorage";
import {
  getEnvironmentProps,
  isBrowser,
  isDmnURL,
  isNativeTab
} from "../utils/environment";
import {
  handleNotificationReceived,
  trySwitchSubscriptionFromNotification
} from "../utils/nativeUtils/notificationReceived";
import {
  handleUniversalLink,
  trySwitchSubscriptionFromLink
} from "../utils/nativeUtils/universalLink";

export function useStartup() {
  const { updateStage } = useLoadingStatus();
  const dispatch = useAppDispatch();
  const subscriptionId = useAppSelector(selectSubscriptionId);
  const connectionStatus = useAppSelector(selectConnectionStatus);
  const setSubscriptionId = useCallback(
    (id: string) => dispatch(subscriptionIdChanged(id)),
    [dispatch]
  );
  const [status, setStatus] = React.useState<LoadingStatus>("loading");
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const redirectSignInAttempt = parseInt(urlParams.get("att") || "0", 0);

  const connectionStatusStr = connectionStatus;
  useEffect(() => {
    offlineLocalStorageManager({ connectionStatus });
  }, [connectionStatusStr]);

  async function loadCollinsUser() {
    updateStage("authentication", "loading");

    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);

    if (!token) {
      updateStage("authentication", "error", {
        message: "Unable to authenticate. Please try logging in again.",
        technicalDetails: "No access token found"
      });
      return null;
    }

    try {
      const response = await fetch(`${environmentVars?.AUTH_API_BASE_URL}/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response && response.ok) {
        const data = await response.json();
        const user = data.payload as CollinsUser;

        if (!user) {
          updateStage("authentication", "error", {
            message: "User data is incomplete. Please contact support.",
            technicalDetails: "Response payload is empty"
          });
          return null;
        }

        updateStage("authentication", "success");
        return user;
      }

      updateStage("authentication", "error", {
        message: "Failed to load user profile. Please try again.",
        technicalDetails: `HTTP ${response.status}: ${response.statusText}`
      });
      return null;
    } catch (error) {
      updateStage("authentication", "error", {
        message: "Network error while loading user profile.",
        technicalDetails: error instanceof Error ? error.message : String(error)
      });
      console.error(error);
      return null;
    }
  }

  const {
    data: userInfoData,
    isLoading: isUserInfoLoading,
    isError: isUserInfoError,
    error: userInfoError
  } = useQuery<GraphResponse | undefined, Error>({
    queryKey: ["UserInfo"],
    queryFn: getUserInfo,
    enabled: !isDmnURL()
  });

  React.useEffect(() => {
    if (isDmnURL()) {
      return;
    }

    if (isUserInfoLoading) {
      return;
    }

    if (isUserInfoError) {
      updateStage("authentication", "error", {
        message:
          "You are not authorised to use this app, please contact support.",
        technicalDetails:
          userInfoError instanceof Error
            ? userInfoError.message
            : String(userInfoError)
      });
      return;
    }

    if (!userInfoData?.data) {
      updateStage("authentication", "error", {
        message:
          "You are not authorised to use this app, please contact support.",
        technicalDetails: "getUserInfo returned no data"
      });
      return;
    }

    const users = ((userInfoData.data as RootQuery)?.users ?? []) as User[];

    const filteredUsers = users.filter(
      (user) => user.applicationUsers && user.applicationUsers?.length > 0
    );

    if (filteredUsers.length === 0) {
      updateStage("authentication", "error", {
        message:
          "You are not authorised to use this app, please contact support.",
        technicalDetails: "No users with application access found"
      });
      return;
    }

    dispatch(usersChanged(filteredUsers));

    //If we have a subscription id, we can use that.
    if (
      filteredUsers?.[0]?.applicationUsers?.[0]?.applicationSubscription?.id
    ) {
      setSubscriptionId(
        filteredUsers[0].applicationUsers[0].applicationSubscription.id ?? ""
      );
    }
  }, [
    userInfoData,
    isUserInfoLoading,
    isUserInfoError,
    userInfoError,
    updateStage, // (important-comment) Safe: updateStage is memoized with useCallback in useLoadingStatus
    dispatch,
    setSubscriptionId
  ]);

  async function finishLoading() {
    if (isNativeTab()) {
      document.documentElement.style.setProperty(
        "--workspace-tab-adjustment",
        `0px`
      );
    }

    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token) ?? null;
    const collinsUser = await loadCollinsUser();

    if (!collinsUser) {
      if (redirectSignInAttempt < 3) {
        try {
          urlParams.set("att", (redirectSignInAttempt + 1).toString());
          const returnUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;

          if (environmentVars?.BROWSER_SIGN_IN_URL) {
            window.location.assign(
              `${environmentVars.BROWSER_SIGN_IN_URL}?returnUrl=${encodeURIComponent(returnUrl)}`
            );
            return;
          }
        } catch (error) {
          console.error("Error during auth redirect:", error);
        }
      }

      errorHandler(
        new Error("User details could not be found."),
        "Error fetching user details"
      );

      urlParams.delete("att");
      const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, document.title, newUrl);

      return;
    }

    urlParams.delete("att");
    const newUrl = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, document.title, newUrl);

    dmnIdentity.isAccessIdentityEnabled =
      collinsUser.access_identity_enabled ?? false;

    dispatch(collinsUserChanged(collinsUser));
    dmnStorage.add("user", collinsUser);

    const isAccessButtonEnabledForEnv =
      environmentVars?.IS_ACCESS_BUTTON_ENABLED !== undefined &&
      environmentVars.IS_ACCESS_BUTTON_ENABLED;

    setStatus("success");
    ui.hideLoadingIndicator();
    dispatch(
      accessButtonEnabledChanged(
        isAccessButtonEnabledForEnv &&
          !isNativeTab() &&
          isBrowser() &&
          !!collinsUser?.workspace_id &&
          !!collinsUser.workspace_organisation_id
      )
    );

    dispatch(
      accessButtonMenuItemsChanged(
        environmentVars?.ACCESS_BUTTON_MENU_ITEMS ?? "products"
      )
    );

    // call the application loaded event to tell the device we are ready.
    // Any additional parameters will be passed back such as any notification link pressed.
    lifecycle.loaded().then(async (results: LoadedInfo) => {
      //The native device will pass back some information about the device which may be useful.
      //The top padding is only passed from Android devices, as the media queries for top spacing does not always work. This allows conistency.
      if (results.paddingForTopBar) {
        document.documentElement.style.setProperty(
          "--topPadding",
          `${results.paddingForTopBar}px`
        );
        document.documentElement.style.setProperty(
          "--safe-area-inset-top",
          `${results.paddingForTopBar}px`
        );
      }
      document.documentElement.setAttribute(
        "data-device-version",
        results.platformInfo.deviceVersion
      );
      document.documentElement.setAttribute(
        "data-device-platform",
        results.platformInfo.devicePlatform
      );
      document.documentElement.setAttribute(
        "data-platform-version",
        results.platformInfo.platformVersion
      );

      //update the height css variable. 1% of screen height
      if (results.screenInfo) {
        setScreenHeight(results.screenInfo.screenHeight);
      }

      //Current connection status passed in the load state, update
      dispatch(connectionStatusChanged(results.connectionStatus));

      dispatch(biometricEnabledChanged(results?.biometricSettings?.enabled));
      dispatch(
        biometricSupportedChanged(results?.biometricSettings?.supported)
      );
      dispatch(pinSecureChanged(results?.pinSecure));
      dispatch(
        displayOrientationChanged(results?.screenInfo.displayOrientation)
      );
    });
  }

  const setScreenHeight = (screenHeight: number) => {
    //update the height css variable. 1% of screen height
    document.documentElement.style.setProperty(
      "--vh",
      `${screenHeight * 0.01}px`
    );
  };

  const onConnectionChange = (newStatus: ConnectionStatus) => {
    dispatch(connectionStatusChanged(newStatus));
  };

  const onScreenChangeFunction = (args: ScreenInfo) => {
    setScreenHeight(args.screenHeight);
    dispatch(displayOrientationChanged(args.displayOrientation));
  };

  const appNotificationReceived = async (payload: MobileNotification) => {
    const shouldHandle = await trySwitchSubscriptionFromNotification(
      payload,
      subscriptionId,
      setSubscriptionId
    );

    if (shouldHandle) {
      await handleNotificationReceived(payload);
    }
  };

  const appResumeFunction = async (args: OnResumeInfo) => {
    //update the height css variable. 1% of screen height
    setScreenHeight(args.screenInfo.screenHeight);
    //update the biometric status and supported
    dispatch(biometricEnabledChanged(args?.biometricSettings?.enabled));
    dispatch(biometricSupportedChanged(args?.biometricSettings?.supported));
    //inform the bridge that we are ready to remove the overlay
    ui.hideLoadingIndicator();

    //if the application was resumed by clicking on a notification, this will be passed as "notification" in the resume event args.
    if (args.notification) {
      const shouldHandle = await trySwitchSubscriptionFromNotification(
        args.notification,
        subscriptionId,
        setSubscriptionId
      );

      if (shouldHandle) {
        await handleNotificationReceived(args.notification);
      }
    }

    //if the application was resumed by clicking on a universal hyperlink, this will be passed as "universalLink" in the resume event args.
    if (args.universalLink) {
      const shouldHandle = await trySwitchSubscriptionFromLink(
        args.universalLink,
        subscriptionId,
        setSubscriptionId
      );

      if (shouldHandle) {
        await handleUniversalLink(args.universalLink, navigate);
      }
    }
  };
  React.useEffect(() => {
    finishLoading();
  }, []);

  const onConnectionChangedOnline = () => {
    onConnectionChange("internet");
  };
  const onConnectionChangedOffline = () => {
    onConnectionChange("none");
  };

  React.useEffect(() => {
    if (isBrowser()) {
      window.addEventListener("online", onConnectionChangedOnline);
      window.addEventListener("offline", onConnectionChangedOffline);
    } else {
      //hook into the connection changing event
      device.onConnectionChanged.addListener(onConnectionChange);
    }

    //Add my on resume callback function to the native app
    lifecycle.onResume.addListener(appResumeFunction);
    lifecycle.onNotification.addListener(appNotificationReceived);
    //hook into the screen changing, e.g. orientation
    device.onScreenChange.addListener(onScreenChangeFunction);

    return () => {
      //clean up on unmount to remove the events listeners
      lifecycle.onResume.removeListener(appResumeFunction);
      lifecycle.onNotification.removeListener(appNotificationReceived);
      device.onScreenChange.removeListener(onScreenChangeFunction);
      if (isBrowser()) {
        window.removeEventListener("online", onConnectionChangedOnline);
        window.removeEventListener("offline", onConnectionChangedOffline);
      } else {
        device.onConnectionChanged.removeListener(onConnectionChange);
      }
    };
  }, [subscriptionId]);

  return { status };
}
